import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isPlatformAdminUser } from '../lib/platformAdmin';
import ImageRetention from '../components/ImageRetention';
import AuditTrail from '../components/AuditTrail';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';
import { ChevronDown, ChevronRight, ExternalLink, FileSignature, IdCard, Link as LinkIcon, Copy, Check, Monitor, Plus, ScanFace, Search, Settings2, ShieldCheck, UserCheck, UserPlus, UserRound, MessageSquare, Ban, X } from 'lucide-react';

const fmt = (v) => (v == null || v === '' ? '—' : v);

function formatDateOnly(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

function formatDateTime(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const normalized = number <= 1 ? number * 100 : number;
  return `${Math.round(normalized)}%`;
}

function formatMatch(value) {
  if (value === true) return 'Match';
  if (value === false) return 'Mismatch';
  return 'Unknown';
}

function formatDocumentCheck(status) {
  if (status === 'approved') return 'Passed';
  if (['pending', 'opened', 'in_progress', 'processing', 'needs_review', 'needs_action'].includes(status)) return 'Needs review';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(status)) return 'Failed';
  return 'Unknown';
}

function resultTone(value) {
  if (['Match', 'Passed'].includes(value)) return 'success';
  if (value === 'Needs review') return 'pending';
  if (['Mismatch', 'Failed'].includes(value)) return 'error';
  return 'neutral';
}

function faceMatchTone(score, status) {
  if (score === null || score === undefined || score === '') return 'pending';
  if (status === 'approved') return 'success';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(status)) return 'error';
  if (['pending', 'opened', 'in_progress', 'processing', 'needs_review', 'needs_action'].includes(status)) return 'pending';
  return 'neutral';
}

function humanizeDocumentType(value) {
  if (!value) return null;
  return String(value)
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function splitOcrName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: null, lastName: parts[0] };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

// Normalize MRZ uppercase names to title case for display
function normalizeMrzNameDisplay(mrzName) {
  if (!mrzName) return null;
  const name = String(mrzName).trim();
  if (!name) return null;
  return name.split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function mrzIsCanonical(ocrPayload) {
  const mrz = ocrPayload?.mrz;
  // Use MRZ when it's valid and checksum-valid (cryptographically trusted)
  return Boolean(mrz?.mrz_valid === true && mrz?.checksum_valid === true && (mrz?.surname || mrz?.given_names));
}

function canonicalIdentityFromEvidence(session, ocrPayload) {
  const displaySplit = splitOcrName(ocrPayload?.display_name);
  const splitName = splitOcrName(session.extracted_name);
  const mrz = ocrPayload?.mrz || {};
  const useMrz = mrzIsCanonical(ocrPayload);
  
  // If backend already applied MRZ canonicalization (mrz_merge_applied), use OCR fields directly
  // Otherwise, apply canonicalization here
  const backendMrzApplied = ocrPayload?.canonical_identity_source === 'mrz' || ocrPayload?.mrz_merge_applied;
  
  if (backendMrzApplied) {
    // Backend already merged; use OCR fields which contain MRZ-normalized values
    return {
      firstName: displaySplit.firstName || ocrPayload?.first_name || splitName.firstName,
      lastName: displaySplit.lastName || ocrPayload?.last_name || splitName.lastName,
      dob: session.extracted_dob || ocrPayload?.dob,
      documentNumber: session.extracted_document_number || ocrPayload?.document_number,
      expiryDate: ocrPayload?.expiry_date,
      nationality: ocrPayload?.nationality,
      displayName: ocrPayload?.display_name || session.extracted_name,
      displayNameSource: ocrPayload?.display_name_source || ocrPayload?.name_reconciliation_source,
      nameReconciliationReason: ocrPayload?.name_reconciliation_reason,
    };
  }
  
  // Fallback: canonicalize on frontend if backend didn't
  return {
    firstName: displaySplit.firstName || (useMrz && mrz.given_names ? normalizeMrzNameDisplay(mrz.given_names) : (ocrPayload?.first_name || splitName.firstName)),
    lastName: displaySplit.lastName || (useMrz && mrz.surname ? normalizeMrzNameDisplay(mrz.surname) : (ocrPayload?.last_name || splitName.lastName)),
    dob: useMrz && mrz.birth_date ? mrz.birth_date : session.extracted_dob,
    documentNumber: useMrz && mrz.document_number ? mrz.document_number : session.extracted_document_number,
    expiryDate: useMrz && mrz.expiry_date ? mrz.expiry_date : ocrPayload?.expiry_date,
    nationality: useMrz && mrz.nationality ? mrz.nationality : ocrPayload?.nationality,
    displayName: ocrPayload?.display_name || session.extracted_name,
    displayNameSource: ocrPayload?.display_name_source || ocrPayload?.name_reconciliation_source,
    nameReconciliationReason: ocrPayload?.name_reconciliation_reason,
  };
}

function latestEventPayload(events, type) {
  return [...events].reverse().find((event) => event.type === type)?.payload || null;
}

function extractNationalRegisterNumber(rawText) {
  const match = String(rawText || '').match(/\b(\d{2})[.\s](\d{2})[.\s](\d{2})[-\s]?(\d{3})[.\s](\d{2})\b/);
  return match ? `${match[1]}.${match[2]}.${match[3]}-${match[4]}.${match[5]}` : null;
}

function isInternalReference(value) {
  const reference = String(value || '').trim();
  if (!reference) return false;

  return [
    /^signature:[0-9a-f-]{36}$/i,
    /^verification:[0-9a-f-]{36}$/i,
    /^session:[0-9a-f-]{36}$/i,
  ].some((pattern) => pattern.test(reference));
}

function displayReference(value) {
  if (!value || isInternalReference(value)) return null;
  return value;
}

function hasValue(value) {
  if (React.isValidElement(value)) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
}

function Field({ label, value, mono, hideEmpty = false, noDivider = false }) {
  if (hideEmpty && !hasValue(value)) return null;
  return (
    <div className={`grid grid-cols-1 gap-1 py-3 ${noDivider ? '' : 'border-b border-zinc-100 last:border-0'} sm:grid-cols-[minmax(160px,176px)_minmax(0,1fr)] sm:gap-4`}>
      <div className="min-w-0 text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`min-w-0 break-words ${mono ? 'break-all font-mono text-sm' : 'text-sm'}`}>{fmt(value)}</div>
    </div>
  );
}

const resultBadgeStyles = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  warning: 'bg-amber-50 text-amber-800 border-amber-300',
  error: 'bg-red-50 text-red-700 border-red-300',
  neutral: 'bg-white text-zinc-500 border-zinc-200',
};

function ResultBadge({ value, tone }) {
  if (!hasValue(value)) return null;
  const resolvedTone = tone || resultTone(value);
  return (
    <span className={`inline-flex h-5 items-center gap-1 px-1.5 uppercase tracking-widest text-[10px] font-mono border ${resultBadgeStyles[resolvedTone] || resultBadgeStyles.neutral}`}>
      {resolvedTone === 'success' && <Check className="w-3 h-3" strokeWidth={1.8}/>}
      {value}
    </span>
  );
}

const checklistIndicatorStyles = {
  success: 'bg-green-50 text-green-700 border-green-300',
  warning: 'bg-amber-50 text-amber-700 border-amber-300',
  error: 'bg-red-50 text-red-700 border-red-300',
  pending: 'bg-zinc-50 text-zinc-500 border-zinc-300',
  neutral: 'bg-zinc-50 text-zinc-500 border-zinc-300',
};

function ChecklistIndicator({ tone, shape = 'round' }) {
  const resolvedTone = tone || 'neutral';
  const shapeClass = shape === 'square' ? 'rounded-sm h-7 w-7' : 'rounded-full h-6 w-6';
  return (
    <span className={`inline-flex shrink-0 items-center justify-center border ${shapeClass} ${checklistIndicatorStyles[resolvedTone] || checklistIndicatorStyles.neutral}`}>
      {resolvedTone === 'success' && <Check className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'error' && <X className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'pending' && <span className="h-3 w-3 rounded-full border border-current border-t-transparent opacity-70 animate-spin"/>}
      {(resolvedTone === 'neutral' || resolvedTone === 'warning') && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>}
    </span>
  );
}

function verificationHasActiveProgress(status, hasEvidence) {
  const normalized = String(status || '').toLowerCase();
  return Boolean(hasEvidence) || ['needs_review', 'needs_action', 'in_progress', 'processing'].includes(normalized);
}

function verificationIsWaitingForApplicant(status) {
  const normalized = String(status || '').toLowerCase();
  return !['approved', 'completed', 'rejected', 'revoked', 'expired', 'failed'].includes(normalized);
}

function hasDocumentSigningActivity(status) {
  const normalized = String(status || '').toLowerCase();
  return ['signing_started', 'in_progress', 'processing'].includes(normalized);
}

function checklistToneFromStatus(status, flowReady = true, hasActiveProgress = false) {
  const normalized = String(status || '').toLowerCase();
  if (['approved', 'completed', 'signed', 'finalized'].includes(normalized)) return 'success';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(normalized)) return 'error';
  return 'pending';
}

function mergeChecklistTones(tones) {
  const values = tones.filter(Boolean);
  if (values.includes('error')) return 'error';
  if (values.includes('pending')) return 'pending';
  if (values.includes('warning')) return 'warning';
  if (values.every((tone) => tone === 'success')) return 'success';
  if (values.includes('success')) return 'success';
  return values[0] || 'neutral';
}

function verificationMethodFromSession(session) {
  const method = String(session?.verification_method || session?.method || '').toLowerCase();
  if (method === 'idv' || method === 'id_only' || session?.requires_selfie === false) return 'idv';
  return 'kyc';
}

function verificationMethodDisplay(method) {
  return method === 'idv' ? 'Basic IDV' : 'Basic KYC';
}

function verificationMethodFlowName(method) {
  return method === 'idv' ? 'identity verification' : 'KYC verification';
}

function normalizeDeviceIpAnalysis(session) {
  if (!session?.device_ip_analysis_enabled) return null;
  const analysis = session.device_ip_analysis && typeof session.device_ip_analysis === 'object'
    ? session.device_ip_analysis
    : {};
  return analysis;
}

function deviceIpAnalysisCaptured(analysis) {
  return Boolean(analysis?.captured_at || analysis?.server?.captured_at || analysis?.client?.captured_at);
}

function verificationWaitingCopy(session) {
  const method = verificationMethodFlowName(verificationMethodFromSession(session));
  return `Waiting for the applicant to complete the ${method} flow.`;
}

function WorkflowStatusNode({ tone, shape = 'round' }) {
  return <ChecklistIndicator tone={tone} shape={shape}/>;
}

function WorkflowStepCard({ label, description, tone, shape = 'round', badge, icon: Icon, variant = 'primary', linked = false }) {
  const isSubcheck = variant === 'subcheck';
  const cardStyle = isSubcheck
    ? 'rounded-full border-zinc-200 bg-white px-3 py-2'
    : 'rounded-md border-zinc-200 bg-white px-3 py-2.5';
  const borderStyle = linked ? 'border-dashed' : 'border-solid';
  return (
    <div className={`min-w-0 border ${borderStyle} ${cardStyle}`}>
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-700 ${isSubcheck ? 'font-normal' : 'font-semibold'}`}>
              {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.7}/>}
              {label}
            </span>
            {badge && (
              <span className="inline-flex h-5 items-center rounded-full border border-zinc-200 bg-white px-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {badge}
              </span>
            )}
          </div>
          {description && <div className="mt-1.5 text-xs text-zinc-500 leading-relaxed">{description}</div>}
        </div>
        <div className="shrink-0">
          <WorkflowStatusNode tone={tone} shape={shape}/>
        </div>
      </div>
    </div>
  );
}

function WorkflowBranchRow({ children }) {
  return (
    <div className="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)] items-center gap-0">
      <span aria-hidden="true" className="h-px bg-zinc-200/90"/>
      <div className="min-w-0 w-full sm:w-[88%]">{children}</div>
    </div>
  );
}

function WorkflowConnector({ children, connectsToNext = false }) {
  return (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-0">
      <div className="flex justify-end">
        <span aria-hidden="true" className="w-px bg-zinc-200/90"/>
      </div>
      <div className={`min-w-0 pt-3 ${connectsToNext ? 'pb-0' : 'pb-3'}`}>
        {children}
      </div>
    </div>
  );
}

function WorkflowConnectorSegment() {
  return (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-0">
      <div className="flex justify-end">
        <span aria-hidden="true" className="h-3 w-px bg-zinc-200/90"/>
      </div>
      <div aria-hidden="true"/>
    </div>
  );
}

function ageRequirementChecklistState(session) {
  const minAge = Number(session?.min_age || 0);
  if (!Number.isFinite(minAge) || minAge <= 0) return null;

  const ageCheck = String(session?.age_check || '').toLowerCase();
  if (ageCheck === 'passed') return { badge: `${minAge}+`, tone: 'success' };
  if (ageCheck === 'failed') return { badge: `${minAge}+`, tone: 'error' };
  return { badge: `${minAge}+`, tone: 'neutral' };
}

function verificationChecklistDescription(status, waitingCopy, method = 'kyc') {
  const normalized = String(status || '').toLowerCase();
  const label = method === 'idv' ? 'Identity verification' : 'KYC verification';
  if (['approved', 'completed'].includes(normalized)) return `${label} completed successfully.`;
  if (normalized === 'rejected') return `${label} was rejected.`;
  if (normalized === 'revoked') return `${label} has been revoked.`;
  if (normalized === 'expired') return `${label} has expired.`;
  if (normalized === 'failed') return `${label} failed.`;
  return waitingCopy || 'Waiting for the applicant to complete the identity verification flow.';
}

function linkedDocumentChecklistDescription(status, setupReady = true, verificationComplete = true) {
  const normalized = String(status || '').toLowerCase();
  if (['signed', 'completed', 'finalized'].includes(normalized)) return 'Document signing completed successfully.';
  if (normalized === 'revoked') return 'Document signing has been revoked.';
  if (normalized === 'expired') return 'Document signing has expired.';
  if (normalized === 'failed' || normalized === 'rejected') return 'Document signing failed.';
  if (setupReady && !verificationComplete) return 'Waiting for linked KYC verification to be completed.';
  if (!setupReady || !verificationComplete || !hasDocumentSigningActivity(normalized)) return 'Document signing has not started yet.';
  return 'Document signing is in progress.';
}

function linkedDocumentChecklistTone(status, setupReady = true, verificationComplete = true) {
  const normalized = String(status || '').toLowerCase();
  if (['signed', 'completed', 'finalized'].includes(normalized)) return 'success';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(normalized)) return 'error';
  if (!setupReady || !verificationComplete) return 'neutral';
  return hasDocumentSigningActivity(normalized) ? 'pending' : 'neutral';
}

function linkedDocumentSetupReadyFromStatus(status) {
  const normalized = String(status || '').toLowerCase();
  if (['signed', 'completed', 'finalized', 'revoked', 'expired', 'failed', 'rejected'].includes(normalized)) return true;
  return !['', 'draft', 'setup_required', 'ready_to_send', 'not_started', 'created'].includes(normalized);
}

function VerificationChecklistCard({
  verificationStatus,
  verificationTone,
  nameCheckLabel,
  dobCheckLabel,
  documentCheckLabel,
  faceMatchBadgeTone,
  nameCheckTone,
  dobCheckTone,
  documentCheckTone,
  selfieCheckTone,
  ageRequirement,
  isLinkedToSignature,
  linkedSignatureStatus,
  flowReady,
  linkedDocumentSetupReady,
  verificationActive,
  verificationComplete,
  verificationWaitingMessage,
  verificationMethod,
  showSelfieStep = true,
  deviceIpAnalysis,
}) {
  const verificationStatusIsTerminal = ['approved', 'completed', 'rejected', 'revoked', 'expired', 'failed']
    .includes(String(verificationStatus || '').toLowerCase());
  const checksReady = flowReady || verificationStatusIsTerminal;
  const checkTone = (tone) => {
    const resolvedTone = checksReady ? tone : 'neutral';
    return ['pending', 'warning'].includes(resolvedTone) ? 'neutral' : resolvedTone;
  };
  const idDocumentTone = mergeChecklistTones([
    documentCheckTone || resultTone(documentCheckLabel),
    nameCheckTone || resultTone(nameCheckLabel),
    dobCheckTone || resultTone(dobCheckLabel || 'Unknown'),
  ]);
  const setupStep = {
    label: 'Setup',
    description: 'Verification setup completed successfully.',
    tone: 'success',
    shape: 'square',
    icon: Settings2,
  };
  const deviceIpStep = deviceIpAnalysis ? {
    key: 'device-ip-analysis',
    label: 'Device and IP analysis',
    tone: deviceIpAnalysisCaptured(deviceIpAnalysis) ? 'success' : 'neutral',
    shape: 'round',
    icon: Monitor,
  } : null;
  const verificationStep = {
    label: 'Verification',
    description: verificationChecklistDescription(verificationStatus, verificationWaitingMessage, verificationMethod),
    tone: verificationTone,
    shape: 'square',
    icon: UserCheck,
  };
  const subchecks = [
    ...(deviceIpStep ? [deviceIpStep] : []),
    {
      key: 'id-document',
      label: 'ID document',
      tone: checkTone(idDocumentTone),
      shape: 'round',
      icon: IdCard,
    },
    ...(ageRequirement ? [{
      key: 'age-requirement',
      label: 'Age requirement',
      badge: ageRequirement.badge,
      tone: checkTone(ageRequirement.tone),
      shape: 'round',
      icon: ShieldCheck,
    }] : []),
    ...(showSelfieStep ? [{
      key: 'selfie',
      label: 'Selfie',
      tone: checkTone(selfieCheckTone || faceMatchBadgeTone),
      shape: 'round',
      icon: ScanFace,
    }] : []),
  ];
  const linkedDocumentStep = isLinkedToSignature ? {
    label: 'Linked document',
    description: linkedDocumentChecklistDescription(linkedSignatureStatus, linkedDocumentSetupReady, verificationComplete),
    tone: linkedDocumentChecklistTone(linkedSignatureStatus, linkedDocumentSetupReady, verificationComplete),
    shape: 'square',
    icon: FileSignature,
    linked: true,
  } : null;

  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Workflow</h2>
      <p className="text-sm text-zinc-500 mt-0.5">Current verification workflow status.</p>
      <div className="mt-5">
        <WorkflowStepCard {...setupStep}/>
        <WorkflowConnector connectsToNext>
          <div className="space-y-3">
            {subchecks.map((item) => (
              <WorkflowBranchRow key={item.key}>
                <WorkflowStepCard key={item.key} {...item} variant="subcheck"/>
              </WorkflowBranchRow>
            ))}
          </div>
        </WorkflowConnector>
        <WorkflowConnectorSegment/>
        <WorkflowStepCard {...verificationStep}/>
        {linkedDocumentStep && (
          <>
            <WorkflowConnectorSegment/>
            <WorkflowStepCard {...linkedDocumentStep}/>
          </>
        )}
      </div>
    </ReportCard>
  );
}

function ReportCard({ children, className = '' }) {
  return (
    <section className={`border border-zinc-200 rounded-sm bg-white p-6 ${className}`}>
      {children}
    </section>
  );
}

function RetentionNotice({ children }) {
  return (
    <p className="mt-4 text-sm text-zinc-500 leading-relaxed border border-zinc-100 bg-zinc-50/60 rounded-sm px-3 py-2">
      {children}
    </p>
  );
}

function LinkedDocumentValue({ title, signatureId }) {
  const action = signatureId ? (
    <Link
      to={`/dashboard/signatures/${signatureId}`}
      className="inline-flex h-8 shrink-0 items-center gap-2 border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 hover:border-zinc-400"
    >
      Open document
    </Link>
  ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="min-w-0 break-words text-sm text-zinc-900">{title || 'Document signing flow'}</div>
        <div className="mt-1 text-xs text-zinc-500">Related signing workflow</div>
      </div>
      {action}
    </div>
  );
}

function LinkedReferenceBox({ label, children }) {
  if (!children) return null;

  return (
    <div className="mt-3 rounded-lg border border-dashed border-zinc-300 bg-white p-4 transition-colors">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-3 min-w-0">{children}</div>
    </div>
  );
}

function VerificationUrlValue({ url }) {
  if (!url) return 'Not available';
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={url}
      className="block max-w-full truncate font-mono text-sm text-zinc-900 hover:underline"
    >
      {url}
    </a>
  );
}

function KybLinkBadge({ businessId }) {
  const className = 'inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400';
  const content = (
    <>
      <LinkIcon className="w-3 h-3" strokeWidth={1.5}/>
      KYB
    </>
  );
  if (!businessId) return <span className={className}>{content}</span>;
  return (
    <Link to={`/dashboard/businesses/${businessId}`} className={className}>
      {content}
    </Link>
  );
}

function LinkedBusinessValue({ businessName, businessId }) {
  if (!businessName && !businessId) return null;
  const label = businessName || 'Linked KYB record';
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 leading-tight">
      {businessId ? (
        <Link to={`/dashboard/businesses/${businessId}`} className="min-w-0 break-words leading-tight hover:underline">
          {label}
        </Link>
      ) : (
        <span className="min-w-0 break-words leading-tight">{label}</span>
      )}
      <KybLinkBadge businessId={businessId}/>
    </div>
  );
}

function hasVerificationEvidence(session, events) {
  const evidenceEventTypes = new Set([
    'upload.received',
    'ocr.completed',
    'ocr.failed',
    'ocr.front.completed',
    'document.front.uploaded',
    'document.back.uploaded',
    'face_match.completed',
    'face_match.failed',
    'identity_claims.checked',
    'sandbox.simulated',
    'mrz.extraction.started',
    'mrz.extraction.completed',
    'mrz.validation.failed',
    'mrz.validation.passed',
    'document.retry.required',
  ]);
  const hasEvidenceEvent = events.some((event) => evidenceEventTypes.has(event.type));
  const hasExtractedFields = [
    session.extracted_name,
    session.extracted_dob,
    session.extracted_document_number,
    session.extracted_document_type,
    session.extracted_country,
    session.face_match_score,
    session.applicant_age,
    session.age_check,
  ].some((value) => value !== null && value !== undefined && value !== '');
  return hasEvidenceEvent || hasExtractedFields;
}

function hasEvidenceAttempt(events) {
  return eventOccurred(events, [
    'upload.received',
    'ocr.failed',
    'ocr.front.completed',
    'document.front.uploaded',
    'document.back.uploaded',
    'mrz.extraction.started',
    'mrz.validation.failed',
    'document.retry.required',
    'face_match.failed',
  ]);
}

function eventOccurred(events, types) {
  const typeSet = new Set(types);
  return events.some((event) => typeSet.has(event.type));
}

function normalizeEvidenceToneForLifecycle(status, tone) {
  const normalized = String(status || '').toLowerCase();
  if (['revoked', 'expired'].includes(normalized) && tone === 'error') return 'neutral';
  return tone;
}

function documentEvidenceTone(session, events, ocrPayload) {
  const documentEvidenceCompleted = eventOccurred(events, [
    'ocr.completed',
    'mrz.extraction.completed',
    'mrz.validation.passed',
  ]) || [
    ocrPayload?.raw,
    session.extracted_name,
    session.extracted_dob,
    session.extracted_document_number,
    session.extracted_document_type,
    session.extracted_country,
  ].some((value) => value !== null && value !== undefined && value !== '');

  if (eventOccurred(events, ['document.validation.failed'])) return 'error';
  return documentEvidenceCompleted ? 'success' : 'neutral';
}

function selfieEvidenceTone(session, events) {
  const selfieAttempted = eventOccurred(events, ['face_match.completed', 'face_match.failed'])
    || session.face_match_score !== null
    && session.face_match_score !== undefined
    && session.face_match_score !== '';
  if (!selfieAttempted) return 'neutral';
  if (eventOccurred(events, ['face_match.failed'])) return 'error';
  if (['rejected', 'failed'].includes(String(session.status || '').toLowerCase()) && Number(session.face_match_score) < 0.3) return 'error';
  return 'success';
}

function matchEvidenceTone(matchValue, evidencePresent) {
  if (matchValue === true) return 'success';
  if (matchValue === false) return 'error';
  return evidencePresent ? 'neutral' : 'neutral';
}

function DetectionSignals({ signals }) {
  if (!signals || signals.length === 0) return null;
  return (
    <div className="text-xs space-y-1">
      {signals.map((sig, i) => (
        <div key={i} className="font-mono text-zinc-700">• {sig}</div>
      ))}
    </div>
  );
}

function AdvancedDisclosure({ open, onToggle, description, children }) {
  return (
    <div className="mt-8 border border-zinc-200 rounded-sm">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span>
          <span className="block font-display text-lg font-semibold">Advanced details</span>
          <span className="block text-sm text-zinc-500 mt-0.5">{description}</span>
        </span>
        {open
          ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={1.5}/>
          : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={1.5}/>}
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-zinc-200">
          {children}
        </div>
      )}
    </div>
  );
}

function ContactDetailsCard({ displayName, email, phoneNumber }) {
  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Contact details</h2>
      <div className="mt-3">
        <Field label="Applicant" value={displayName || 'Not provided'}/>
        <Field label="Email" value={email || 'Not provided'} mono/>
        <Field label="Phone number" value={phoneNumber || 'Not provided'} mono/>
      </div>
    </ReportCard>
  );
}

function VerificationSummaryCard({ verificationId, verificationUrl, createdAt, methodLabel, linkedDocumentValue }) {
  const hasLinkedDocument = hasValue(linkedDocumentValue);

  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Verification summary</h2>
      <div className="mt-3">
        <Field label="Verification ID" value={verificationId || 'Not available'} mono/>
        <Field label="Verification URL" value={<VerificationUrlValue url={verificationUrl}/>}/>
        <Field label="Created at" value={formatDateTime(createdAt)} mono/>
        <Field label="Method" value={methodLabel || 'Basic KYC'} noDivider={hasLinkedDocument}/>
        {hasLinkedDocument && (
          <LinkedReferenceBox label="Linked document">
            {linkedDocumentValue}
          </LinkedReferenceBox>
        )}
      </div>
    </ReportCard>
  );
}

const deviceRiskTone = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
};

const deviceFlagLabels = {
  missing_user_agent: 'Missing user agent',
  missing_timezone: 'Missing timezone',
  headless_like_environment: 'Headless-like environment',
  very_small_viewport: 'Very small viewport',
  language_missing: 'Language missing',
  multiple_sessions_same_ip: 'Multiple sessions from same IP',
  multiple_sessions_same_user_agent: 'Multiple sessions from same browser',
  multiple_applicants_same_ip: 'Multiple applicants from same IP',
};

const identityEvidenceSourceLabels = {
  document_selfie: 'ID document + selfie',
  document_only: 'ID document',
  manual_review: 'Manual review',
  eudi_wallet: 'EUDI Wallet',
  mygov_wallet: 'MyGov.be / EUDI Wallet',
  itsme: 'itsme',
  mock_wallet: 'Mock wallet',
  business_registry: 'Business registry',
  ubo_declaration: 'UBO declaration',
};

const identityEvidenceStatusTone = {
  pending: 'neutral',
  verified: 'success',
  failed: 'error',
  expired: 'error',
  rejected: 'error',
};

const identityEvidenceAttributeLabels = {
  given_name: 'Given name',
  family_name: 'Family name',
  birth_date: 'Birth date',
  nationality: 'Nationality',
  document_number: 'Document number',
  document_type: 'Document type',
  age_over_18: 'Age over 18',
  age_over_21: 'Age over 21',
  unique_identifier: 'Unique identifier',
};

function browserLabel(userAgent) {
  const ua = String(userAgent || '');
  if (!ua) return 'Not available';
  if (/Edg\//.test(ua)) return 'Microsoft Edge';
  if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Other browser';
}

function formatScreenValue(analysis) {
  const client = analysis?.client || {};
  const screen = client.screen_width && client.screen_height
    ? `${client.screen_width}×${client.screen_height}`
    : null;
  const viewport = client.viewport_width && client.viewport_height
    ? `${client.viewport_width}×${client.viewport_height} viewport`
    : null;
  return [screen, viewport].filter(Boolean).join(' · ') || 'Not available';
}

function RiskSignalBadges({ flags = [] }) {
  if (!flags.length) return 'None detected';
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((flag) => (
        <span key={flag} className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {deviceFlagLabels[flag] || String(flag).replaceAll('_', ' ')}
        </span>
      ))}
    </div>
  );
}

function DeviceIpAnalysisCard({ analysis }) {
  if (!analysis) return null;
  const client = analysis.client || {};
  const riskLevel = analysis.risk_level || (deviceIpAnalysisCaptured(analysis) ? 'Low' : 'Pending capture');
  const userAgent = analysis.user_agent || analysis.server?.user_agent || client.user_agent;
  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Device and IP analysis</h2>
      <div className="mt-3">
        <Field
          label="Risk level"
          value={<ResultBadge value={riskLevel} tone={deviceRiskTone[riskLevel] || 'neutral'}/>}
        />
        <Field label="IP address" value={analysis.ip_address || analysis.server?.ip_address || 'Not available'} mono/>
        <Field label="Device" value={analysis.device_type || client.device_type || client.platform || 'Not available'}/>
        <Field label="Browser" value={browserLabel(userAgent)}/>
        <Field label="Timezone" value={client.timezone || 'Not available'} mono/>
        <Field label="Language" value={client.language || 'Not available'} mono/>
        <Field label="Screen" value={formatScreenValue(analysis)} mono/>
        <Field label="Captured at" value={formatDateTime(analysis.captured_at || analysis.server?.captured_at || client.captured_at)} mono/>
        <Field label="Risk signals" value={<RiskSignalBadges flags={analysis.flags || []}/>} noDivider/>
      </div>
    </ReportCard>
  );
}

function IdentityEvidenceAttributes({ attributes }) {
  const keys = Array.isArray(attributes)
    ? attributes
    : Object.keys(attributes || {});
  if (!keys.length) return 'Not available';
  return (
    <div className="flex flex-wrap gap-1.5">
      {keys.map((key) => (
        <span key={key} className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {identityEvidenceAttributeLabels[key] || String(key).replaceAll('_', ' ')}
        </span>
      ))}
    </div>
  );
}

function IdentityEvidenceCard({ session }) {
  const evidences = Array.isArray(session?.identity_evidences) ? session.identity_evidences : [];
  const primary = evidences[0] || session?.primary_identity_evidence || null;
  const sourceType = primary?.source_type;
  const status = primary?.status || 'pending';
  const provider = primary?.provider_name || (
    sourceType === 'document_selfie' || sourceType === 'document_only'
      ? 'idemGO document flow'
      : null
  );
  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Identity evidence</h2>
      <p className="mt-1 text-sm leading-relaxed text-zinc-500">
        The source used to support this verification decision. idemGO keeps the workflow separate from the evidence source so wallet-based verification can be added later.
      </p>
      <div className="mt-3">
        <Field label="Source" value={sourceType ? identityEvidenceSourceLabels[sourceType] || sourceType : 'Legacy verification evidence'}/>
        <Field
          label="Status"
          value={<ResultBadge value={status.replaceAll('_', ' ')} tone={identityEvidenceStatusTone[status] || 'neutral'}/>}
        />
        <Field label="Assurance level" value={primary?.assurance_level || 'Unknown'}/>
        <Field label="Provider" value={provider || 'Not provided'}/>
        <Field label="Verified at" value={formatDateTime(primary?.verified_at)} mono/>
        <Field label="Requested attributes" value={<IdentityEvidenceAttributes attributes={primary?.attributes_json}/>} noDivider/>
      </div>
      <p className="mt-4 border border-zinc-100 bg-zinc-50/60 px-3 py-2 text-sm leading-relaxed text-zinc-500">
        Fallback-ready: document, manual, and wallet evidence can support the same KYC or IDV workflow without becoming a new verification method.
      </p>
    </ReportCard>
  );
}

function normalizedCompare(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();
}

function MrzCrossCheckCard({ ocrPayload, canonicalIdentity, session, identityCheck }) {
  const mrz = ocrPayload?.mrz;
  const cmp = ocrPayload?.mrz_comparison;
  if (!cmp) return null;

  const finalIdentity = canonicalIdentity || {};
  const finalFirst = finalIdentity.firstName || ocrPayload?.first_name;
  const finalLast = finalIdentity.lastName || ocrPayload?.last_name;
  const finalDob = finalIdentity.dob || ocrPayload?.dob;
  const finalDocumentNumber = finalIdentity.documentNumber || ocrPayload?.document_number;
  const finalExpiryDate = finalIdentity.expiryDate || ocrPayload?.expiry_date;
  const finalNationality = finalIdentity.nationality || ocrPayload?.nationality;
  const finalNameMatchesMrz = normalizedCompare(finalLast) === normalizedCompare(mrz?.surname)
    && normalizedCompare(finalFirst) === normalizedCompare(mrz?.given_names);
  const approvedOrCompleted = ['approved', 'completed'].includes(String(session?.status || '').toLowerCase());
  const claimsMatched = identityCheck?.result === 'match'
    || (identityCheck?.first_name_match === true && identityCheck?.last_name_match === true && identityCheck?.dob_match === true);
  const suppressFinalNameNoise = approvedOrCompleted && claimsMatched && finalNameMatchesMrz;
  const mismatches = new Set(cmp?.mismatches || []);
  if (suppressFinalNameNoise) {
    mismatches.delete('surname');
    mismatches.delete('given_names');
  }
  const visiblePassed = cmp.passed || (suppressFinalNameNoise && mismatches.size === 0);
  const normalized = ocrPayload?.display_name_source === 'front_ocr_reconciled'
    || ocrPayload?.name_reconciliation_source === 'front_ocr_reconciled';
  const row = (label, ocrVal, mrzVal, key) => {
    const bad = mismatches.has(key);
    return (
      <div
        key={key}
        className={`grid grid-cols-3 gap-3 py-3 border-b border-zinc-100 last:border-0 ${bad ? 'bg-red-50/80' : ''}`}
      >
        <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
        <div className={`col-span-1 text-sm font-mono ${bad ? 'text-red-800' : ''}`}>{fmt(ocrVal)}</div>
        <div className={`col-span-1 text-sm font-mono ${bad ? 'text-red-800' : ''}`}>{fmt(mrzVal)}</div>
      </div>
    );
  };

  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">Front vs coded strip</h2>
      <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">Final reconciled identity compared with the machine-readable strip on the back.</p>
      {cmp && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ResultBadge value={visiblePassed ? 'Consistent' : 'Mismatch'} tone={visiblePassed ? 'success' : 'error'}/>
          {hasValue(cmp.score) && (
            <ResultBadge value={`Score ${(Number(cmp.score) * 100).toFixed(0)}%`} tone="neutral"/>
          )}
          {mrz?.checksum_valid === false && (
            <ResultBadge value="Strip check failed" tone="error"/>
          )}
          {normalized && (
            <ResultBadge value="Name normalized" tone="neutral"/>
          )}
        </div>
      )}
      {normalized && (
        <p className="mt-2 text-xs leading-relaxed text-zinc-500">
          Raw OCR/MRZ values were normalized using final document evidence and claimed identity.
        </p>
      )}
      <div className="mt-4 grid grid-cols-3 gap-3 text-[10px] uppercase tracking-widest text-zinc-400 border-b border-zinc-200 pb-2">
        <span>Field</span>
        <span>Front OCR</span>
        <span>Coded strip</span>
      </div>
      <div className="mt-0">
        {row('Last name', finalLast, mrz?.surname, 'surname')}
        {row('First names', finalFirst, mrz?.given_names, 'given_names')}
        {row('Date of birth', formatDateOnly(finalDob), formatDateOnly(mrz?.birth_date), 'dob')}
        {row('Document number', finalDocumentNumber, mrz?.document_number, 'document_number')}
        {row('Expiry', formatDateOnly(finalExpiryDate), formatDateOnly(mrz?.expiry_date), 'expiry_date')}
        {row('Nationality', finalNationality, mrz?.nationality, 'nationality')}
      </div>
    </ReportCard>
  );
}

function NoEvidenceCard({ session, hadAttempt = false }) {
  const normalized = String(session.status || '').toLowerCase();
  let message = 'No identity documents or selfie evidence were uploaded for this session.';
  if (normalized === 'expired' && !hadAttempt) {
    message = 'This verification session expired before identity documents were submitted.';
  } else if (hadAttempt) {
    message = 'Submitted evidence did not result in a completed verification record.';
  } else if (normalized === 'revoked') {
    message = 'This verification session was revoked before usable identity evidence was received.';
  } else if (['pending', 'opened'].includes(normalized)) {
    message = 'No identity documents or selfie evidence have been uploaded for this session yet.';
  }

  return (
    <ReportCard>
      <h2 className="font-display text-lg font-semibold">No verification evidence received</h2>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{message}</p>
    </ReportCard>
  );
}

const verificationBadgeStyles = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-300',
  needs_action: 'bg-amber-50 text-amber-800 border-amber-300',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  opened: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  in_progress: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  processing: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  expired: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
};

const verificationBadgeLabels = {
  approved: 'Approved',
  rejected: 'Rejected',
  needs_review: 'Action required',
  pending: 'Pending',
  opened: 'Pending',
  in_progress: 'Pending',
  processing: 'Pending',
  expired: 'Expired',
  revoked: 'Revoked',
};

function verificationHeaderStatusLabel(status, displayStatus) {
  const normalized = String(status || 'pending').toLowerCase();
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'needs_review') return 'Action required';
  if (['rejected', 'failed'].includes(normalized)) return 'Rejected';
  if (['revoked', 'expired'].includes(normalized)) return normalized;
  if (['pending', 'opened', 'in_progress', 'processing'].includes(normalized)) return 'Pending';
  return displayStatus || verificationBadgeLabels[normalized] || normalized.replaceAll('_', ' ');
}

function VerificationStatusBadge({ status, displayStatus }) {
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${verificationBadgeStyles[status] || verificationBadgeStyles.pending}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {displayStatus || verificationBadgeLabels[status] || String(status || 'pending').replaceAll('_', ' ')}
    </span>
  );
}

function navigatorApplicantName(item) {
  const claimedName = [item?.claimed_first_name, item?.claimed_last_name].filter(Boolean).join(' ');
  const isOnboarding = item?.purpose === 'onboarding_identity' && item?.is_platform_verification;
  return isOnboarding
    ? (item?.extracted_name || claimedName || item?.applicant_email || 'Unnamed applicant')
    : (claimedName || item?.extracted_name || item?.applicant_email || 'Unnamed applicant');
}

function navigatorSubtitle(item) {
  return item?.applicant_email || item?.extracted_document_number || item?.id || 'No identifier';
}

function navigatorUpdatedAt(item) {
  const value = item?.updated_at || item?.created_at;
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function navigatorAgeLabel(value) {
  const age = Number(value);
  return [16, 18, 21].includes(age) ? `${age}+` : '';
}

function NavigatorAgeBadge({ value, compact = false }) {
  const label = navigatorAgeLabel(value);
  if (!label) return null;
  return (
    <span className={`inline-flex items-center justify-center rounded-full border border-zinc-200 bg-white font-mono text-[10px] tracking-widest text-zinc-500 ${compact ? 'h-5 min-w-5 px-1' : 'h-5 px-1.5'}`} title={`Age requirement: ${label}`} aria-label={`Age requirement: ${label}`}>
      {label}
    </span>
  );
}

function NavigatorMethodBadge({ item }) {
  const method = verificationMethodFromSession(item);
  const label = method === 'idv' ? 'IDV' : 'KYC';
  const Icon = method === 'idv' ? IdCard : UserCheck;
  return (
    <span className="inline-flex h-5 items-center gap-1 rounded-full border border-zinc-200 bg-white px-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
      <Icon className="h-3 w-3" strokeWidth={1.8}/>
      {label}
    </span>
  );
}

function NavigatorWorkflowStepBadge({ icon: Icon, title, variant = 'default', children }) {
  const variantClass = variant === 'external'
    ? 'rounded-md border-dashed border-zinc-200 bg-white text-zinc-400'
    : 'rounded-full border-zinc-200 bg-white text-zinc-400';
  return (
    <span className={`inline-flex h-5 min-w-5 items-center justify-center border px-1 ${variantClass}`} title={title} aria-label={title}>
      {Icon ? <Icon className="h-3 w-3" strokeWidth={1.8}/> : children}
    </span>
  );
}

function NavigatorWorkflowBadges({ item }) {
  const idOnly = verificationMethodFromSession(item) === 'idv';
  const linkedToDocument = !!(
    item?.linked_signature_id ||
    item?.signature_id ||
    item?.linked_document_id ||
    item?.related_type === 'signature'
  );
  const chips = [
    item?.device_ip_analysis_enabled && <NavigatorWorkflowStepBadge key="device" icon={Monitor} title="Device and IP analysis"/>,
    <NavigatorWorkflowStepBadge key="id" icon={IdCard} title="ID document"/>,
    navigatorAgeLabel(item?.min_age) && <NavigatorAgeBadge key="age" value={item?.min_age} compact/>,
    !idOnly && <NavigatorWorkflowStepBadge key="selfie" icon={ScanFace} title="Selfie"/>,
    linkedToDocument && <NavigatorWorkflowStepBadge key="linked-doc" icon={FileSignature} title="Linked document" variant="external"/>,
  ].filter(Boolean);

  return (
    <span className="inline-flex items-center">
      {chips.map((chip, index) => (
        <React.Fragment key={chip.key || index}>
          {index > 0 && <span aria-hidden="true" className="h-px w-1.5 bg-zinc-200"/>}
          {chip}
        </React.Fragment>
      ))}
    </span>
  );
}

function navigatorStatusMeta(status) {
  const normalized = String(status || 'pending').toLowerCase();
  if (['approved', 'verified', 'completed'].includes(normalized)) {
    return { label: 'Approved', tone: 'success', icon: 'check' };
  }
  if (['rejected', 'failed'].includes(normalized)) {
    return { label: 'Failed', tone: 'error', icon: 'x' };
  }
  if (['needs_review', 'needs_action', 'review'].includes(normalized)) {
    return { label: 'Needs review', tone: 'warning', icon: 'dot' };
  }
  if (['expired', 'revoked'].includes(normalized)) {
    return { label: normalized === 'expired' ? 'Expired' : 'Revoked', tone: 'error', icon: 'x' };
  }
  return { label: verificationBadgeLabels[normalized] || normalized.replaceAll('_', ' ') || 'Pending', tone: 'neutral', icon: 'dot' };
}

function NavigatorStatusIndicator({ status }) {
  const meta = navigatorStatusMeta(status);
  const toneClass = {
    success: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    error: 'border-red-300 bg-red-50 text-red-700',
    warning: 'border-amber-300 bg-amber-50 text-amber-800',
    neutral: 'border-zinc-200 bg-white text-zinc-400',
  }[meta.tone] || 'border-zinc-200 bg-white text-zinc-400';
  return (
    <span
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${toneClass}`}
      title={meta.label}
      aria-label={meta.label}
    >
      {meta.icon === 'check' && <Check className="h-3 w-3" strokeWidth={2}/>}
      {meta.icon === 'x' && <X className="h-3 w-3" strokeWidth={2}/>}
      {meta.icon === 'dot' && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-75"/>}
    </span>
  );
}

function VerificationDetailNavigator({ currentId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/verification-sessions', { params: { limit: 50 } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.items) ? data.items : [];
        setItems(rows.slice(0, 50));
      })
      .catch(() => {
        if (!cancelled) setError('Could not load verifications');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredItems = items.filter((item) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [
      navigatorApplicantName(item),
      navigatorSubtitle(item),
      item?.status,
      verificationMethodFromSession(item),
      item?.min_age,
    ].join(' ').toLowerCase().includes(needle);
  });

  return (
    <aside className="hidden w-72 shrink-0 border-r border-zinc-100 bg-white lg:sticky lg:top-16 lg:flex lg:h-[calc(100vh-4rem)] lg:flex-col xl:w-80" data-testid="verification-detail-navigator">
      <div className="flex flex-col justify-center border-b border-zinc-100 bg-white px-3 py-3 lg:min-h-[104px]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate font-display text-lg font-semibold">Individuals</h2>
            <p className="mt-0.5 truncate text-xs text-zinc-500">
              {loading ? 'Loading verifications' : `${items.length} verifications`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard/verifications/new')}
            title="New verification"
            aria-label="New verification"
            className="grid h-7 w-7 shrink-0 place-items-center border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.8}/>
          </button>
        </div>
        <div className="mt-3 flex h-8 items-center gap-2 border border-zinc-200 bg-white px-2.5">
          <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5}/>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-zinc-700 outline-none placeholder:text-zinc-400"
            placeholder="Search"
            aria-label="Search verifications"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="px-3 py-8 text-sm text-zinc-500">Loading…</div>
        ) : error ? (
          <div className="px-3 py-8 text-sm text-zinc-500">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="px-3 py-8 text-sm text-zinc-500">No verifications found.</div>
        ) : (
          <div className="space-y-1">
            {filteredItems.map((item) => {
              const active = item.id === currentId;
              const name = navigatorApplicantName(item);
              const subtitle = navigatorSubtitle(item);
              const updatedAt = navigatorUpdatedAt(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (!active) navigate(`/dashboard/verifications/${item.id}`);
                  }}
                  className={`w-full border px-3 py-2.5 text-left transition-colors ${active ? 'border-zinc-300 bg-white shadow-sm' : 'border-transparent bg-transparent hover:border-zinc-200 hover:bg-white'}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-zinc-900" title={name}>{name}</div>
                      <div className="mt-1 truncate text-xs text-zinc-500" title={subtitle}>{subtitle}</div>
                    </div>
                    {updatedAt && <div className="shrink-0 pt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{updatedAt}</div>}
                  </div>
                  <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <NavigatorMethodBadge item={item}/>
                      <NavigatorWorkflowBadges item={item}/>
                    </div>
                    <NavigatorStatusIndicator status={item.status}/>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}

const docSignBadgeStyles = {
  active: 'bg-white text-zinc-700 border-zinc-200',
  signed: 'bg-white text-zinc-700 border-zinc-200',
  failed: 'bg-white text-zinc-700 border-zinc-200',
};

function docSignBadgeForVerification(session, signatureId) {
  if (!signatureId) return null;
  if (['signed', 'completed'].includes(session.linked_signature_status)) {
    return { label: 'Doc signed', tone: 'signed' };
  }
  if (['rejected', 'failed'].includes(session.linked_signature_status)) {
    return { label: 'Doc rejected', tone: 'failed' };
  }
  if (session.linked_signature_status === 'revoked') {
    return { label: 'Doc revoked', tone: 'failed' };
  }
  if (session.linked_signature_status === 'expired') {
    return { label: 'Doc expired', tone: 'failed' };
  }
  return { label: 'Doc pending', tone: 'active' };
}

function lifecycleBannerForVerification(session, isLinkedToSignature, waitingCopy) {
  const linkedStatus = String(session.linked_signature_status || '').toLowerCase();
  const status = String(session.status || 'pending').toLowerCase();
  const isIdv = verificationMethodFromSession(session) === 'idv';
  const label = isIdv ? 'IDV verification' : 'KYC verification';

  if (status === 'revoked') {
    return {
      key: 'revoked',
      tone: 'error',
      primary: `This ${label} has been revoked.`,
      secondary: isLinkedToSignature
        ? 'Verification can no longer be completed and the linked document can no longer be signed.'
        : 'Verification can no longer be completed.',
    };
  }

  if (status === 'expired') {
    if (linkedStatus === 'revoked') {
      return {
        key: 'expired',
        tone: 'error',
        primary: `This ${label} has expired.`,
        secondary: 'Verification can no longer be completed because the linked document was revoked.',
      };
    }

    if (isLinkedToSignature && linkedStatus === 'expired') {
      return {
        key: 'expired',
        tone: 'error',
        primary: `This ${label} has expired.`,
        secondary: 'Verification was not completed in time and the linked document can no longer be signed.',
      };
    }

    return {
      key: 'expired',
      tone: 'error',
      primary: `This ${label} has expired.`,
      secondary: 'Verification can no longer be completed.',
    };
  }

  if (['rejected', 'failed'].includes(status)) {
    return {
      key: 'rejected',
      tone: 'error',
      primary: `This ${label} has been rejected.`,
      secondary: 'Verification can no longer be completed.',
    };
  }

  if (status === 'needs_review') {
    return {
      key: 'needs_review',
      tone: 'warning',
      primary: 'Manual review required',
      secondary: 'The verification was completed, but idemGO could not confidently match one or more identity claims.',
    };
  }

  if (['approved', 'completed'].includes(status)) {
    return {
      key: 'approved',
      tone: 'success',
      primary: `This ${label} has been approved.`,
      secondary: isIdv
        ? 'Claimed identity matched document evidence.'
        : 'Claimed identity matched document evidence and selfie check passed.',
    };
  }

  return {
    key: 'created',
    tone: 'neutral',
    primary: 'Verification session created',
    secondary: waitingCopy || 'Waiting for the applicant to complete the identity verification flow.',
  };
}

const verificationBannerStyles = {
  success: 'border-emerald-300 bg-emerald-50 text-emerald-700 [&_.secondary]:text-emerald-700',
  neutral: 'border-zinc-200 bg-zinc-50 text-zinc-700 [&_.secondary]:text-zinc-600',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 [&_.secondary]:text-amber-800',
  error: 'border-red-300 bg-red-50 text-red-700 [&_.secondary]:text-red-700',
};

function DocSignBadge({ badge, signatureId }) {
  if (!badge) return null;
  const className = `inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border hover:border-zinc-400 hover:bg-zinc-50 ${docSignBadgeStyles[badge.tone] || docSignBadgeStyles.active}`;
  if (!signatureId) {
    return (
      <span className={className}>
        <LinkIcon className="w-3 h-3" strokeWidth={1.5}/>
        {badge.label}
      </span>
    );
  }

  return (
    <Link to={`/dashboard/signatures/${signatureId}`} className={className}>
      <LinkIcon className="w-3 h-3" strokeWidth={1.5}/>
      {badge.label}
    </Link>
  );
}

const AGE_BADGE = {
  passed:  'bg-emerald-50 text-emerald-700 border-emerald-300',
  failed:  'bg-red-50 text-red-700 border-red-300',
  unknown: 'bg-zinc-50 text-zinc-600 border-zinc-200',
};
function AgeCheckBadge({ check }) {
  if (!check) return null;
  return (
    <span data-testid={`age-check-${check}`}
      className={`text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border ${AGE_BADGE[check] || AGE_BADGE.unknown}`}>
      Age check · {check}
    </span>
  );
}

export default function VerificationDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const isPlatformAdmin = isPlatformAdminUser(user);
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [actLoading, setActLoading] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [identityAction, setIdentityAction] = useState(null);
  const [idemIdentity, setIdemIdentity] = useState(null);
  const [idemIdentityLoading, setIdemIdentityLoading] = useState(false);
  const [summaryHeight, setSummaryHeight] = useState(null);
  const [selfieImagesAvailable, setSelfieImagesAvailable] = useState(null);
  const [documentImagesAvailable, setDocumentImagesAvailable] = useState(null);
  const summaryCardRef = useRef(null);
  const manualReviewRef = useRef(null);

  useEffect(() => {
    setSelfieImagesAvailable(null);
    setDocumentImagesAvailable(null);
    setIdentityAction(null);
    setIdemIdentity(null);
  }, [id]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/verification-sessions/${id}`);
    setData(data);
  }, [id]);
  const handleSelfieAvailability = useCallback((available) => {
    setSelfieImagesAvailable(available);
  }, []);
  const handleDocumentAvailability = useCallback((available) => {
    setDocumentImagesAvailable(available);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const node = summaryCardRef.current;
    if (!node) return undefined;

    const measure = () => setSummaryHeight(node.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [data]);
  useEffect(() => {
    api.get('/organization').then(({ data }) => setMembers(data.members)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!isPlatformAdmin) {
      setIdemIdentity(null);
      setIdemIdentityLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIdemIdentityLoading(true);
    api.get(`/idem-identities/admin/identities/by-verification/${id}`)
      .then(({ data: identityResult }) => {
        if (!cancelled) setIdemIdentity(identityResult.identity || null);
      })
      .catch(() => {
        if (!cancelled) setIdemIdentity(null);
      })
      .finally(() => {
        if (!cancelled) setIdemIdentityLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, isPlatformAdmin]);

  if (!data) return <div className="text-sm text-zinc-500" data-testid="detail-loading">Loading…</div>;
  const { session, events } = data;
  const publicUrl = `${process.env.REACT_APP_BACKEND_URL.replace('/api','')}/v/${session.token}`;
  const verificationMethod = verificationMethodFromSession(session);
  const deviceIpAnalysis = normalizeDeviceIpAnalysis(session);
  const claimedName = [session.claimed_first_name, session.claimed_last_name].filter(Boolean).join(' ');
  const displayName = claimedName || session.applicant_email || 'Unnamed applicant';
  const applicantPhone = session.applicant_phone || session.applicant_phone_number || session.phone_number || session.phone || null;
  const verificationUrl = session.public_url || (session.token ? publicUrl : null);
  const breadcrumbName = displayName;
  const isOnboarding = session.purpose === 'onboarding_identity' && session.is_platform_verification;
  const linkUsable = !['expired', 'revoked'].includes(session.status);
  const identityCheck = latestEventPayload(events, 'identity_claims.checked');
  const ocrPayload = latestEventPayload(events, 'ocr.completed');
  const mrzBackDiagnostics = ocrPayload?.mrz_back_diagnostics
    || latestEventPayload(events, 'mrz.validation.failed')
    || latestEventPayload(events, 'document.retry.required')
    || latestEventPayload(events, 'mrz.extraction.completed');
  const canonicalIdentity = canonicalIdentityFromEvidence(session, ocrPayload);
  const hasEvidence = hasVerificationEvidence(session, events);
  const personalNumber = ocrPayload?.personal_number || extractNationalRegisterNumber(ocrPayload?.raw);
  const firstNameMatch = identityCheck?.first_name_match;
  const lastNameMatch = identityCheck?.last_name_match;
  const hasNameMatch = firstNameMatch !== undefined || lastNameMatch !== undefined || identityCheck?.result;
  const nameMatch = hasNameMatch
    ? (firstNameMatch === true && lastNameMatch === true
      ? true
      : firstNameMatch === false || lastNameMatch === false
        ? false
        : null)
    : null;
  const nameCheckLabel = formatMatch(nameMatch);
  const dobCheckLabel = identityCheck ? formatMatch(identityCheck.dob_match) : null;
  const documentCheckLabel = formatDocumentCheck(session.status);
  const faceMatchBadgeTone = faceMatchTone(session.face_match_score, session.status);
  const nameCheckTone = normalizeEvidenceToneForLifecycle(session.status, matchEvidenceTone(nameMatch, hasNameMatch));
  const dobCheckTone = normalizeEvidenceToneForLifecycle(session.status, matchEvidenceTone(identityCheck?.dob_match, Boolean(identityCheck)));
  const documentCheckTone = normalizeEvidenceToneForLifecycle(session.status, documentEvidenceTone(session, events, ocrPayload));
  const selfieCheckTone = normalizeEvidenceToneForLifecycle(session.status, selfieEvidenceTone(session, events));
  const rawAgeRequirement = ageRequirementChecklistState(session);
  const ageRequirement = rawAgeRequirement
    ? { ...rawAgeRequirement, tone: normalizeEvidenceToneForLifecycle(session.status, rawAgeRequirement.tone) }
    : null;
  const requiresSelfieEvidence = verificationMethod !== 'idv';
  const createdMode = new URLSearchParams(location.search).get('created');
  const createdEmail = new URLSearchParams(location.search).get('email');
  const applicantReferenceIsSignature = String(session.applicant_reference || '').startsWith('signature:');
  const linkedSignatureId = session.linked_signature_id
    || (applicantReferenceIsSignature ? String(session.applicant_reference).slice('signature:'.length) : null);
  const docSignBadge = docSignBadgeForVerification(session, linkedSignatureId);
  const linkedDocumentTitle = session.linked_signature_title || 'Document signing flow';
  const canReview = ['admin', 'reviewer'].includes(user?.role);
  const isLinkedToSignature = !!session.linked_signature_id
    || String(session.applicant_reference || '').startsWith('signature:');
  const linkedDocumentSetupReady = linkedDocumentSetupReadyFromStatus(session.linked_signature_status);
  const verificationFlowReady = !isLinkedToSignature || linkedDocumentSetupReady;
  const verificationActive = verificationHasActiveProgress(session.status, hasEvidence)
    || (isLinkedToSignature && linkedDocumentSetupReady && verificationIsWaitingForApplicant(session.status));
  const verificationComplete = ['approved', 'completed'].includes(String(session.status || '').toLowerCase());
  const verificationChecklistTone = checklistToneFromStatus(session.status, verificationFlowReady, verificationActive);
  const linkedDocumentValue = isLinkedToSignature
    ? <LinkedDocumentValue title={linkedDocumentTitle} signatureId={linkedSignatureId}/>
    : null;
  const linkedBusinessId = session.linked_business_verification_id || null;
  const linkedBusinessValue = (session.linked_business_verification_id || session.linked_business_name || session.linked_representative_id)
    ? <LinkedBusinessValue businessName={session.linked_business_name} businessId={linkedBusinessId}/>
    : null;
  const hasRetainedImages = Number(session.retained_image_count || 0) > 0;
  const shouldRenderSelfiePreview = hasRetainedImages && selfieImagesAvailable !== false;
  const shouldRenderDocumentPreviews = hasRetainedImages && documentImagesAvailable !== false;
  const shouldShowSelfieRetentionNotice = !shouldRenderSelfiePreview && (selfieCheckTone !== 'neutral' || requiresSelfieEvidence);
  const shouldShowDocumentRetentionNotice = !shouldRenderDocumentPreviews && documentCheckTone !== 'neutral';
  const canReopenReview = canReview
    && ['approved', 'rejected'].includes(session.status)
    && !isLinkedToSignature
    && hasRetainedImages;
  const showManualReviewControls = canReview && session.status === 'needs_review';
  const verificationWaitingMessage = verificationWaitingCopy(session, isLinkedToSignature);
  const hadEvidenceAttempt = hasEvidenceAttempt(events);
  const identityDetailValues = [
    canonicalIdentity.firstName,
    canonicalIdentity.lastName,
    canonicalIdentity.dob,
    ocrPayload?.gender,
    canonicalIdentity.nationality,
    personalNumber,
  ];
  const documentDetailValues = [
    ocrPayload?.issuing_country || session.document_country || session.extracted_country,
    session.extracted_document_type,
    canonicalIdentity.documentNumber,
    ocrPayload?.issue_date,
    canonicalIdentity.expiryDate,
  ];
  const hasIdentityDetails = identityDetailValues.some(hasValue);
  const hasDocumentDetails = documentDetailValues.some(hasValue);
  const hasCodedStripDetails = Boolean(ocrPayload?.mrz_comparison);
  const hasUsableEvidence = hasIdentityDetails || hasDocumentDetails || hasCodedStripDetails;
  const verificationBanner = lifecycleBannerForVerification(session, isLinkedToSignature, verificationWaitingMessage);
  const verificationStatusBanner = verificationBanner ? (
    <div className={`border px-4 py-3 text-sm ${verificationBannerStyles[verificationBanner.tone] || verificationBannerStyles.warning}`}>
      <div className="font-medium">{verificationBanner.primary}</div>
      <div className="secondary mt-1">{verificationBanner.secondary}</div>
    </div>
  ) : null;
  const verificationCreatedBanner = createdMode ? (
    <div className={`border px-4 py-3 text-sm ${
      createdMode === 'email_failed'
        ? 'border-amber-300 bg-amber-50 text-amber-800'
        : 'border-emerald-300 bg-emerald-50 text-emerald-700'
    }`}>
	      {createdMode === 'email'
	        ? `Invitation sent${createdEmail ? ` to ${createdEmail}` : ''}.`
	        : createdMode === 'email_failed'
	          ? `${verificationMethod === 'idv' ? 'IDV' : 'KYC'} verification link created, but email could not be sent. Copy and share it manually.`
	          : `${verificationMethod === 'idv' ? 'IDV' : 'KYC'} verification link created. Copy and share it manually.`}
    </div>
  ) : null;
  const verificationTopBanners = (
    <>
      {verificationCreatedBanner}
      {verificationStatusBanner}
    </>
  );
  const verificationChecklist = (
    <VerificationChecklistCard
      verificationStatus={session.status}
      verificationTone={verificationChecklistTone}
      nameCheckLabel={nameCheckLabel}
      dobCheckLabel={dobCheckLabel}
      documentCheckLabel={documentCheckLabel}
      faceMatchBadgeTone={faceMatchBadgeTone}
      nameCheckTone={nameCheckTone}
      dobCheckTone={dobCheckTone}
      documentCheckTone={documentCheckTone}
      selfieCheckTone={selfieCheckTone}
      ageRequirement={ageRequirement}
      isLinkedToSignature={isLinkedToSignature}
      linkedSignatureStatus={session.linked_signature_status}
      flowReady={verificationFlowReady}
	      linkedDocumentSetupReady={linkedDocumentSetupReady}
	      verificationActive={verificationActive}
	      verificationComplete={verificationComplete}
	      verificationWaitingMessage={verificationWaitingMessage}
	      verificationMethod={verificationMethod}
	      showSelfieStep={requiresSelfieEvidence}
	      deviceIpAnalysis={deviceIpAnalysis}
	    />
  );

  const decide = async (status, reason) => {
    setActLoading(true);
    try {
      await api.post(`/verification-sessions/${id}/decision`, { status, reason });
      await load();
    } finally { setActLoading(false); }
  };

  const assign = async (userId) => {
    setActLoading(true);
    try {
      if (userId) await api.post(`/verification-sessions/${id}/assign`, { user_id: userId });
      else await api.delete(`/verification-sessions/${id}/assign`);
      await load();
    } finally { setActLoading(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await api.post(`/verification-sessions/${id}/comment`, { text: comment });
    setComment(''); await load();
  };

  const reopenReview = async () => {
    setActLoading(true);
    try {
      await api.post(`/verification-sessions/${id}/reopen-review`);
      await load();
    } finally { setActLoading(false); }
  };

  const scrollToManualReview = () => {
    manualReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    manualReviewRef.current?.focus?.({ preventScroll: true });
  };

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const canRevoke = ['pending', 'opened'].includes(session.status) && !session.usage_used_at && !session.revoked_at;
  const canCreateIdemIdentity = isPlatformAdmin
    && !idemIdentityLoading
    && !idemIdentity
    && ['approved', 'completed'].includes(String(session.status || '').toLowerCase())
    && ['kyc', 'idv'].includes(String(verificationMethod || '').toLowerCase());
  const createIdemIdentity = async () => {
    setActLoading(true);
    setIdentityAction(null);
    try {
      const { data: identityResult } = await api.post('/idem-identities/admin/identities/from-verification', { verification_id: id });
      const reference = identityResult?.identity?.public_id || 'this verification';
      setIdemIdentity(identityResult?.identity || null);
      setIdentityAction({
        tone: 'success',
        text: identityResult?.created
          ? `idem identity ${reference} created from verification.`
          : `An idem identity already exists for this verification: ${reference}.`,
      });
    } catch (error) {
      setIdentityAction({
        tone: 'error',
        text: error?.response?.data?.error || 'The idem identity could not be created.',
      });
    } finally {
      setActLoading(false);
    }
  };
  const revoke = async () => {
    setActLoading(true);
    try {
      await api.post(`/verification-sessions/${id}/revoke`);
      setRevokeOpen(false);
      await load();
    } finally { setActLoading(false); }
  };
  const manualReviewCard = showManualReviewControls ? (
    <ReportCard>
      <div ref={manualReviewRef} tabIndex={-1} className="outline-none" data-testid="manual-review-panel">
        <h2 className="font-display text-lg font-semibold">Manual review</h2>
        <p className="mt-1 text-sm text-zinc-600">
          Review the identity claim match and choose a final decision. The applicant has already submitted the verification.
        </p>
        <div className="mt-4">
          <h3 className="font-display text-sm font-semibold mb-2">Manual decision</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => decide('approved', 'Manually approved by reviewer')} disabled={actLoading}
              data-testid="decide-approve" className="text-sm px-3 py-1.5 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50">Approve</button>
            <button onClick={() => decide('rejected', 'Manually rejected by reviewer')} disabled={actLoading}
              data-testid="decide-reject" className="text-sm px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50">Reject</button>
            <button onClick={() => decide('needs_review', 'Sent back for review')} disabled={actLoading}
              data-testid="decide-review" className="text-sm px-3 py-1.5 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">Needs review</button>
          </div>
        </div>

        <div className="mt-6 border-t border-zinc-200 pt-4">
          <h3 className="font-display text-sm font-semibold mb-2 flex items-center gap-2">
            <UserPlus className="w-4 h-4" strokeWidth={1.5}/> Reviewer assignment
          </h3>
          <div className="flex flex-wrap items-center gap-2" data-testid="assignment-row">
            <select value={session.assigned_to || ''} onChange={(e)=>assign(e.target.value || null)} disabled={actLoading}
              data-testid="assign-select" className="text-sm border border-zinc-200 rounded-sm px-3 py-1.5">
              <option value="">- Unassigned -</option>
              {members.map((mm) => (
                <option key={mm.id} value={mm.id}>{mm.email} ({mm.role})</option>
              ))}
            </select>
            <button onClick={() => assign(user.id)} disabled={actLoading || session.assigned_to === user.id}
              data-testid="assign-self" className="text-sm px-3 py-1.5 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-40">
              Assign to me
            </button>
            {session.assigned_to_email && (
              <span className="text-xs font-mono text-zinc-500">currently: {session.assigned_to_email}</span>
            )}
          </div>
        </div>

        <div className="mt-6 border-t border-zinc-200 pt-4">
          <h3 className="font-display text-sm font-semibold mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" strokeWidth={1.5}/> Add a comment
          </h3>
          <form onSubmit={submitComment} className="flex gap-2" data-testid="comment-form">
            <input value={comment} onChange={(e)=>setComment(e.target.value)} placeholder="Reviewer note..."
              data-testid="comment-input" className="flex-1 border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
            <button type="submit" disabled={!comment.trim()} data-testid="comment-submit"
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-40">
              Post
            </button>
          </form>
        </div>
      </div>
    </ReportCard>
  ) : null;

  return (
    <div data-testid="verification-detail" className="lg:-mx-8 lg:-my-8 lg:flex lg:min-h-[calc(100vh-4rem)]">
      <VerificationDetailNavigator currentId={id}/>
      <div className="min-w-0 flex-1 lg:px-8 lg:py-8">
      <VerificationBreadcrumb current={breadcrumbName}/>

      <div className="mb-6 bg-white lg:sticky lg:top-16 lg:z-20 lg:-mx-8 lg:-mt-8 lg:border-b lg:border-zinc-100 lg:bg-white/85 lg:px-8 lg:pb-3 lg:pt-5 lg:backdrop-blur-md supports-[backdrop-filter]:lg:bg-white/75">
        <div className="flex min-w-0 flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Verification
            </p>
            <h1 className="mt-1 flex min-w-0 items-start gap-2 font-display text-3xl font-bold tracking-tight">
              <UserRound className="mt-1 h-6 w-6 shrink-0 text-zinc-400" strokeWidth={1.5}/>
              <span className="min-w-0 break-words">{displayName}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <VerificationStatusBadge status={session.status} displayStatus={verificationHeaderStatusLabel(session.status, session.display_status)}/>
              {isOnboarding && (
                <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border border-zinc-200 bg-white text-zinc-500" title="Workspace verification">
                  <Monitor className="w-3 h-3" strokeWidth={1.8}/>
                  Workspace
                </span>
              )}
              <DocSignBadge badge={docSignBadge} signatureId={linkedSignatureId}/>
              {linkedBusinessValue && <KybLinkBadge businessId={linkedBusinessId}/>}
              {session.sandbox && (
                <span data-testid="detail-sandbox-badge"
                  className="text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">
                  Sandbox
                </span>
              )}
              <span className="inline-flex min-w-0 items-center border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                <span className="min-w-0 break-all">ID: {session.id}</span>
              </span>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            {showManualReviewControls && (
              <button onClick={scrollToManualReview} disabled={actLoading}
                className="inline-flex items-center gap-1.5 text-sm border border-amber-300 text-amber-800 rounded-sm px-3 py-2 hover:border-amber-500 disabled:opacity-50">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5}/>
                Review decision
              </button>
            )}
            {canReopenReview && (
              <button onClick={reopenReview} disabled={actLoading}
                className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400 disabled:opacity-50">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5}/>
                Needs review
              </button>
            )}
            {canCreateIdemIdentity && (
              <button onClick={createIdemIdentity} disabled={actLoading}
                className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm rounded-sm hover:border-zinc-400 disabled:opacity-50">
                <UserCheck className="w-4 h-4" strokeWidth={1.5}/>
                Create idem identity
              </button>
            )}
            {isPlatformAdmin && idemIdentity && (
              <Link to={`/internal/identities/${idemIdentity.id}`}
                className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm rounded-sm hover:border-zinc-400">
                <UserCheck className="w-4 h-4" strokeWidth={1.5}/>
                Open idem identity
              </Link>
            )}
            <a href={linkUsable ? publicUrl : undefined} target="_blank" rel="noreferrer" data-testid="open-public-link"
               className={`inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 ${linkUsable ? 'hover:border-zinc-400' : 'opacity-40 pointer-events-none'}`}>
              <ExternalLink className="w-4 h-4" strokeWidth={1.5}/> Open public link
            </a>
            <button onClick={copy} disabled={!linkUsable} data-testid="copy-public-link"
              className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400 disabled:opacity-40 disabled:hover:border-zinc-200">
              {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>} Copy URL
            </button>
            {canRevoke && (
              <button onClick={() => setRevokeOpen(true)} disabled={actLoading}
                className="inline-flex items-center gap-1.5 text-sm border border-red-200 text-red-700 rounded-sm px-3 py-2 hover:border-red-400 disabled:opacity-50">
                <Ban className="w-4 h-4" strokeWidth={1.5}/> Revoke
              </button>
            )}
          </div>
        </div>
      </div>

      {identityAction && (
        <div className={`mb-6 border px-4 py-3 text-sm ${identityAction.tone === 'success'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
          : 'border-red-300 bg-red-50 text-red-700'}`}>
          {identityAction.text}
        </div>
      )}

      {revokeOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={() => setRevokeOpen(false)}>
          <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-xl font-semibold">Revoke link?</h2>
            <p className="text-sm text-zinc-600 mt-2">This action cannot be undone. The recipient will no longer be able to access this link.</p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setRevokeOpen(false)} className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">Cancel</button>
              <button onClick={revoke} disabled={actLoading} className="text-sm px-3 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50">Revoke link</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {verificationTopBanners}
        {manualReviewCard}

        <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] lg:items-start">
          <div ref={summaryCardRef} className="min-w-0 space-y-6">
            <ContactDetailsCard
              displayName={displayName}
              email={session.applicant_email}
              phoneNumber={applicantPhone}
            />
            <VerificationSummaryCard
              verificationId={session.id}
              verificationUrl={verificationUrl}
              createdAt={session.created_at}
              methodLabel={verificationMethodDisplay(verificationMethod)}
              linkedDocumentValue={linkedDocumentValue}
            />
            <IdentityEvidenceCard session={session}/>
            <DeviceIpAnalysisCard analysis={deviceIpAnalysis}/>
            {verificationChecklist}
          </div>
          <AuditTrail
            events={events}
            className="hidden h-full lg:flex"
            previewHeight={summaryHeight}
            expanded={auditExpanded}
            onExpand={() => setAuditExpanded(true)}
            onCollapse={() => setAuditExpanded(false)}
          />
        </div>

        {hasUsableEvidence ? (
        <>
          {hasIdentityDetails && (
            <ReportCard>
              <div className={`grid gap-6 ${shouldRenderSelfiePreview ? 'lg:grid-cols-2 lg:items-stretch' : ''}`}>
                <div>
                  <h2 className="font-display text-lg font-semibold">Identity details</h2>
                  <div className="mt-3">
                    <Field label="First name" value={canonicalIdentity.firstName} hideEmpty/>
                    <Field label="Last name" value={canonicalIdentity.lastName} hideEmpty/>
                    <Field label="Date of birth" value={formatDateOnly(canonicalIdentity.dob)} mono hideEmpty/>
                    <Field label="Gender" value={ocrPayload?.gender} mono hideEmpty/>
                    <Field label="Nationality" value={canonicalIdentity.nationality} mono hideEmpty/>
                    <Field label="Personal number" value={personalNumber} mono hideEmpty/>
                  </div>
                  {shouldShowSelfieRetentionNotice && (
                    <RetentionNotice>Selfie image removed according to retention policy.</RetentionNotice>
                  )}
                </div>
                {shouldRenderSelfiePreview && (
                <div className="w-full self-stretch">
                  <ImageRetention
                    sessionId={id}
                    variant="contextual"
                    kinds={['selfie']}
                    columns="grid-cols-1"
                    onAvailability={handleSelfieAvailability}
                  />
                </div>
                )}
              </div>
            </ReportCard>
          )}

          {hasDocumentDetails && (
            <ReportCard>
              <h2 className="font-display text-lg font-semibold">Document details</h2>
              <div className="mt-3">
                <Field label="Country" value={ocrPayload?.issuing_country || session.document_country || session.extracted_country} mono hideEmpty/>
                <Field label="Document type" value={humanizeDocumentType(session.extracted_document_type)} hideEmpty/>
                <Field label="Document number" value={canonicalIdentity.documentNumber} mono hideEmpty/>
                <Field label="Date of issue" value={formatDateOnly(ocrPayload?.issue_date)} mono hideEmpty/>
                <Field label="Date of expiry" value={formatDateOnly(canonicalIdentity.expiryDate)} mono hideEmpty/>
              </div>
              {shouldShowDocumentRetentionNotice && (
                <RetentionNotice>Document images removed according to retention policy.</RetentionNotice>
              )}

              {shouldRenderDocumentPreviews && (
              <div className="mt-6">
                <ImageRetention
                  sessionId={id}
                  variant="contextual"
                  kinds={['id_document_front', 'id_document_back']}
                  columns="grid-cols-1 sm:grid-cols-2"
                  onAvailability={handleDocumentAvailability}
                />
              </div>
              )}
            </ReportCard>
          )}

          <MrzCrossCheckCard
            ocrPayload={ocrPayload}
            canonicalIdentity={canonicalIdentity}
            session={session}
            identityCheck={identityCheck}
          />

          <ImageRetention sessionId={id} variant="access"/>
        </>
        ) : (
          <NoEvidenceCard session={session} hadAttempt={hadEvidenceAttempt}/>
        )}

        <AuditTrail
          events={events}
          className="flex lg:hidden"
        />
      </div>
      </div>
    </div>
  );
}
